import { InPlay } from '@ps/isp-web-component-library/icons/InPlay';
import React from 'react';

import { useLiveData } from '@swf/sports/providers/LiveDataProvider';
import type { EventTypeId } from '@swf/sports/types/eventType';
import type { LiveData } from '@swf/sports/utils/getLiveData';
import {
  getIsAmericanFootballLiveData,
  getIsBaseballLiveData,
  getIsBasketballLiveData,
  getIsDartsLiveData,
  getIsESportsLiveData,
  getIsFootballLiveData,
  getIsHandballLiveData,
  getIsIceHockeyLiveData,
  getIsSnookerLiveData,
  getIsTableTennisLiveData,
  getIsTennisLiveData,
  getIsVolleyballLiveData
} from '@swf/sports/utils/getLiveData';

import EventStatusMessage from '../EventStatusMessage/EventStatusMessage';

import AmericanFootballScoreboardInPlayStatus from './AmericanFootballScoreboardInPlayStatus/AmericanFootballScoreboardInPlayStatus';
import BaseballScoreboardInPlayStatus from './BaseballScoreboardInPlayStatus/BaseballScoreboardInPlayStatus';
import BasketballScoreboardInPlayStatus from './BasketballScoreboardInPlayStatus/BasketballScoreboardInPlayStatus';
import FootballScoreboardInPlayStatus from './FootballScoreboardInPlayStatus/FootballScoreboardInPlayStatus';
import HandballScoreboardInPlayStatus from './HandballScoreboardInPlayStatus/HandballScoreboardInPlayStatus';
import IceHockeyScoreboardInPlayStatus from './IceHockeyScoreboardInPlayStatus/IceHockeyScoreboardInPlayStatus';
import TennisScoreboardInPlayStatus from './TennisScoreboardInPlayStatus/TennisScoreboardInPlayStatus';

interface Props {
  liveData: LiveData;
  eventTypeId: EventTypeId;
}

const ScoreboardInPlayStatus: React.FC<Props> = ({ liveData, eventTypeId }) => {
  const { isError } = useLiveData();

  if (
    isError ||
    getIsDartsLiveData(eventTypeId, liveData) ||
    getIsESportsLiveData(eventTypeId, liveData) ||
    getIsSnookerLiveData(eventTypeId, liveData) ||
    getIsTableTennisLiveData(eventTypeId, liveData) ||
    getIsVolleyballLiveData(eventTypeId, liveData)
  ) {
    return <EventStatusMessage messageId="IN_PLAY" icon={InPlay} size="XSmall" />;
  }

  if (getIsAmericanFootballLiveData(eventTypeId, liveData)) {
    return <AmericanFootballScoreboardInPlayStatus americanFootballFixture={liveData} />;
  }

  if (getIsBaseballLiveData(eventTypeId, liveData)) {
    return <BaseballScoreboardInPlayStatus baseballFixture={liveData} />;
  }

  if (getIsBasketballLiveData(eventTypeId, liveData)) {
    return <BasketballScoreboardInPlayStatus basketballFixture={liveData} />;
  }

  if (getIsHandballLiveData(eventTypeId, liveData)) {
    return <HandballScoreboardInPlayStatus handballFixture={liveData} />;
  }

  if (getIsFootballLiveData(eventTypeId, liveData)) {
    return <FootballScoreboardInPlayStatus footballFixture={liveData} />;
  }

  if (getIsIceHockeyLiveData(eventTypeId, liveData)) {
    return <IceHockeyScoreboardInPlayStatus iceHockeyFixture={liveData} />;
  }

  if (getIsTennisLiveData(eventTypeId, liveData)) {
    return <TennisScoreboardInPlayStatus tennisMatch={liveData} />;
  }

  return null;
};

export default ScoreboardInPlayStatus;
